import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useLocation } from 'react-router-dom';
import produce from 'immer';
import { debounce } from 'lodash';
import { RootState } from 'modules';
import { useRecoilState, useRecoilValue } from 'recoil';
import { getUrlQuery } from 'libs/utils';
import { TQuizOption, TQuizAnswer, TInputWrapperProps } from 'modules/learning';
import { gradingLoadingState, isGradedState, isResettingDoneState } from 'modules/learning/recoilState';
import { Form, Input, InputNumber, Switch, Select, Button } from 'antd';
import styled from 'styled-components';
import {
  device,
  Colors,
  LightGrayColor,
  StyledCheckIcon,
  StyledCrossIcon,
  StyledIconBackground,
} from 'GlobalStyles';

const KaitusInputWrapper = ({
  col,
  depth,
  indexAnswerState,
  setIndexAnswerState,
  form,
  isShown,
  options,
  setOptions,
  userAnswer,
  setUserAnswer,
  defaultAnswer,
  setDefaultAnswer,
  answer_id,
}: TInputWrapperProps) => {
  const location = useLocation();
  const [isShownChecked, setIsShownChecked] = useState<boolean>(false);
  const { step, step_name, answer_type, operation, id } = getUrlQuery(location.search);
  const [defaultContent, setDefaultContent] = useState<{
    content_number: number;
    content: string;
  }>();
  const quizState = useSelector((store: RootState) => store.createQuizReducer);
  const gradingLoading = useRecoilValue<boolean>(gradingLoadingState);
  const [optionPropsMustAdd, setOptionPropsMustAdd] = useState<TQuizOption>({
    content: '',
    content_number: 0,
  });
  const [isCorrectAnswer, setIsCorrectAnswer] = useState(false);
  const isGraded = useRecoilValue<boolean>(isGradedState);
  const [isResettingDone, setIsResettingDone] = useRecoilState(isResettingDoneState);

  useEffect(() => {
    //등록했던 답안지 정보 보여주기위해(어드민 문제 수정 페이지)
    if (operation !== 'view' && isResettingDone) {
      if (depth === 0) {
        //최상단 row
        const answerNumber = indexAnswerState.columns[col]?.content_number;
        const answerContent = indexAnswerState.columns[col]?.content;
        const isShown = indexAnswerState.columns[col]?.is_shown;

        form.setFields([
          { name: `index_answer_number_${col}_${depth}`, value: answerNumber },
          { name: `index_answer_${col}_${depth}`, value: answerContent },
          { name: `index_answer_is_shown_${col}_${depth}`, value: isShown },
          { name: `index_answer_id_${col}_${depth}`, value: answer_id },
        ]);
        setIsShownChecked(isShown);
      } else if (depth > 0) {
        //최상단 목차의 하위 답안
        const answerNumber = indexAnswerState.columns[col].items[depth - 1]?.content_number;
        const answerContent = indexAnswerState.columns[col].items[depth - 1]?.content;
        const isShown = indexAnswerState.columns[col].items[depth - 1]?.is_shown;

        form.setFields([
          { name: `index_answer_number_${col}_${depth}`, value: answerNumber },
          { name: `index_answer_${col}_${depth}`, value: answerContent },
          { name: `index_answer_is_shown_${col}_${depth}`, value: isShown },
          { name: `index_answer_id_${col}_${depth}`, value: answer_id },
        ]);
        setIsShownChecked(isShown);
      }
      setIsResettingDone(false);
    }
    //사용자에게 디폴트로 보여줘야 하는 답안(isShown===true)은 따로 defaultContent 상태값의 content, content_number 프로퍼티로 관리
    //antd form 으로 value값을 넣어주지 않기 때문에
    if (operation === 'view' && isShown) {
      if (depth === 0) {
        //최상단 row
        const answerNumber = indexAnswerState.columns[col]?.content_number;
        const answerContent = indexAnswerState.columns[col]?.content;
        if (answerNumber && answerContent) {
          setDefaultContent({
            ...defaultContent,
            content_number: answerNumber,
            content: answerContent,
          });
        }
      } else if (depth > 0) {
        //최상단 목차의 하위 답안
        const answerNumber = indexAnswerState.columns[col].items[depth - 1]?.content_number;
        const answerContent = indexAnswerState.columns[col].items[depth - 1]?.content;
        if (answerNumber && answerContent) {
          setDefaultContent({
            ...defaultContent,
            content_number: answerNumber,
            content: answerContent,
          });
        }
      }
    }
  }, [indexAnswerState, isResettingDone]);

  const handleIsShownChecked = (checked: boolean) => {
    setIsShownChecked(checked);
    const nextState = produce(indexAnswerState, (draft) => {
      if (depth === 0) {
        draft.columns[col].is_shown = checked;
      } else if (depth > 0) {
        draft.columns[col].items[depth - 1].is_shown = checked;
      }
    });
    setIndexAnswerState(nextState);
  };

  const onValueChange = debounce(
    (value: { index_answer_number?: number | undefined; index_answer?: string }) => {
      if (value.index_answer_number) {
        const nextState = produce(indexAnswerState, (draft) => {
          if (depth === 0) {
            draft.columns[col].content_number = value.index_answer_number;
          } else if (depth > 0) {
            draft.columns[col].items[depth - 1].content_number = value.index_answer_number;
          }
        });
        setIndexAnswerState(nextState);
      }
      if (value.index_answer) {
        const nextState = produce(indexAnswerState, (draft) => {
          if (depth === 0) {
            draft.columns[col].content = value.index_answer;
          } else if (depth > 0) {
            draft.columns[col].items[depth - 1].content = value.index_answer;
          }
        });
        setIndexAnswerState(nextState);
      }
    },
    1000,
  );

  //사용자가 목차유형 답안 선택할 때마다 답안지 옵션 및 선택지 부분 필터링 필요
  const handleUserSelectOption = (value: number) => {
    const formState = form.getFieldsValue();
    const valuesArr = Object.values(formState).filter((el: any) => el !== undefined);
    const valuesNumArr = valuesArr.map((el: any) => Number(el));
    const defaultAnswerIds = defaultAnswer?.map((el: TQuizAnswer) => el.id);

    const filteredOptions = quizState.options?.filter(
      (option: TQuizOption) =>
        option.id &&
        !valuesNumArr.includes(option.id) && // 사용자가 선택한 답안의 Id값들에 포함되지 않으면서
        !defaultAnswerIds?.includes(option.id), // 디폴트 답변의 Id 값들에도 포함되지 않는 요소로 필터링
    );

    setOptions && setOptions(filteredOptions);
  };

  //=> options가 드롭다운 옵션으로 들어가는데 드롭다운 옵션에 없는 값이 select의 value가 되기 떄문에 실제 뜨는 값이 이상하게 나오는 오류가 있었음
  //이를 해결해주기위해 form의 value에서 해당 칸의 id값을 받아서 그 칸에는 그 id를 가진것을 옵션으로 추가시켜주기
  useEffect(() => {
    if (operation === 'view') {
      //본인의 선택값 value도 select.option 옵션에 넣기 위해
      const inputValue = form.getFieldValue(`index_answer_${col}_${depth}`);
      const addInputProps = userAnswer?.find((el: TQuizOption) => el.id === inputValue);
      //내가 선택한 답이 있다면 그 답에대한 정보가 optionPropsMustAdd 값이 된다
      addInputProps && setOptionPropsMustAdd(addInputProps);
      //내가 선택한 답이 없다면 optionPropsMustAdd값도 없다
      !addInputProps &&
        setOptionPropsMustAdd({
          content: '',
          content_number: 0,
        });

      //정오답 표시
      const findAnswer: any = userAnswer?.find((el: any) => {
        return el.column_index === col && el.depth === depth;
      });
      if (findAnswer?.is_answer) {
        setIsCorrectAnswer(true);
      } else {
        setIsCorrectAnswer(false);
      }
    }
  }, [userAnswer]);

  return (
    <>
      {operation !== 'view' && (
        <StyledInputWrapper className='input_wrapper' style={{ width: '210px' }}>
          <Form.Item name={`index_answer_id_${col}_${depth}`} style={{ width: '20%', display: 'none' }}>
            <InputNumber className='option_input' placeholder='id' />
          </Form.Item>
          <Form.Item name={`index_answer_number_${col}_${depth}`} style={{ width: '25%' }}>
            <InputNumber
              className='option_input'
              placeholder='번호'
              onChange={(e) => {
                onValueChange({ index_answer_number: Number(e) });
              }}
            />
          </Form.Item>
          <Form.Item name={`index_answer_${col}_${depth}`} style={{ width: '40%' }}>
            <Input
              className='option_input'
              placeholder='선택지 입력창'
              onChange={(e) => {
                onValueChange({ index_answer: e.target.value });
              }}
            />
          </Form.Item>
          <Form.Item name={`index_answer_is_shown_${col}_${depth}`}>
            <Switch
              checked={isShownChecked}
              onChange={handleIsShownChecked}
              checkedChildren='ON'
              unCheckedChildren='OFF'
            />
          </Form.Item>
        </StyledInputWrapper>
      )}
      {operation === 'view' && (
        <StyledInputWrapper className='input_wrapper' style={{ width: 'inherit' }}>
          {/* 디폴트 답은 무조건 정답 */}
          {isShown && defaultContent && (
            <StyledDefaultSelectWrapper>
              <Button className='index_default'>{`${defaultContent.content}`}</Button>
            </StyledDefaultSelectWrapper>
          )}
          {/* 디폴트 아닌 값들은 isCorrect에 따라서 정오답 표시 */}
          {!isShown && (
            <StyledSelectAndIconWrapper pop={isCorrectAnswer}>
              <Form.Item name={`index_answer_${col}_${depth}`} style={{ width: 'inherit' }}>
                <Select
                  // optionLabelProp="label"
                  placeholder='선택해주세요.'
                  className='quiz-selector'
                  onChange={(value) => handleUserSelectOption(Number(value))}
                >
                  {options &&
                    optionPropsMustAdd &&
                    [...options, optionPropsMustAdd]
                      .sort(function(a, b) {
                        return a.content_number - b.content_number;
                      })
                      .map(
                        (option: TQuizOption) =>
                          option.id && (
                            <Select.Option value={option.id} key={option.id}>
                              {`${option.content_number} . ${option.content}`}
                            </Select.Option>
                          ),
                      )}
                </Select>
              </Form.Item>
              { /*!gradingLoading && isGraded && isCorrectAnswer && (
                <StyledIconBackground type='green' position='absolute' right='10px' top='13px'>
                  <StyledCheckIcon />
                </StyledIconBackground>
              ) *?}
              { /*!gradingLoading && isGraded && !isCorrectAnswer && (
                <StyledIconBackground type='danger' position='absolute' right='10px' top='13px'>
                  <StyledCrossIcon />
                </StyledIconBackground>
              ) */}
            </StyledSelectAndIconWrapper>
          )}
        </StyledInputWrapper>
      )}
    </>
  );
};

export default KaitusInputWrapper;

type TSelectIconWrapperProps = {
  pop?: boolean;
};

export const StyledSelectAndIconWrapper = styled.div<TSelectIconWrapperProps>`
  ${(props) => props.pop === false && 'z-index: 30;'}
  /* z-index: 100; */
  position: relative;
  width: inherit;
`;

export const StyledDefaultSelectWrapper = styled.div`
  position: relative;
  width: inherit;

  & .ant-btn.index_default {
    width: inherit !important;
    border: 1px solid #d9d9d9 !important;
    background-color: #ffffff !important;
    margin: 0px !important;
    cursor: not-allowed;
  }
  & .ant-select-arrow {
    display: none;
  }
`;

export const StyledInputWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  /* margin: 0 10px 10px 0; */
  & .ant-form-item {
    margin-bottom: 0;
  }
  & .ant-input.option_input,
  .ant-input-number.option_input {
    background-color: ${LightGrayColor} !important;
    border-radius: 8px !important;
    border: 1px solid ${Colors.grey2};
  }

  & .ant-select-single.ant-select-show-arrow .ant-select-selection-item {
    padding-right: 0px;
  }
`;
